import { DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import {
  RdsAvatarComponent,
  RdsBadgeComponent,
  RdsEmptyStateComponent,
  RdsEmptyStateTitleDirective,
  RdsIconComponent,
} from '@rds/angular-components';
import { RdsBadgeColor } from '@rds/core';

import {
  BookingHistoryChangeModel,
  BookingHistoryChangeValueModel,
  BookingHistoryChangesModel,
  BookingHistoryEntryModel,
  BookingHistoryParticipantModel,
  BookingStatusApiResponseEnum,
  statusConverter,
  utcDateToTimezoneDisplayDate,
} from '@bookly/shared';

export interface ChangeValue {
  previous?: string | Date;
  current: string | Date;
  isDate: boolean;
}

export interface ParticipantValue {
  name: string;
  email?: string;
}

/**
 * A change whose sides are whole lists. Rendered full width as two stacked
 * columns joined by an arrow, rather than as a single wrapping line.
 */
export interface ParticipantsChangeValue {
  previous?: ParticipantValue[];
  current: ParticipantValue[];
}

/** How a change leaf is formatted for display. */
type ChangeKind = 'text' | 'date' | 'status' | 'services' | 'participants';

interface FieldConfig {
  label?: string;
  kind?: ChangeKind;
}

interface GroupConfig {
  label?: string;
  fields?: Record<string, FieldConfig>;
}

interface ChangeFieldViewModel {
  key: string;
  label: string;
  value?: ChangeValue;
  participants?: ParticipantsChangeValue;
}

interface ChangeGroupViewModel {
  /** Empty for the ungrouped top-level fields, which render without a header. */
  key: string;
  label: string;
  fields: ChangeFieldViewModel[];
}

interface HistoryEntryViewModel {
  revisionNumber: number;
  header: string;
  updatedBy: string;
  updateDate: Date;
  statusText: string;
  statusColor: RdsBadgeColor;
  groups: ChangeGroupViewModel[];
}

/**
 * Presentation overrides for the top-level fields the API sends today.
 *
 * The component renders whatever the payload contains — a field that is not
 * listed here still shows up, with its label derived from the key
 * (`onBehalfParticipants` → `On Behalf Participants`) and its kind inferred
 * from the value. Add an entry only to give a field a nicer label or to force
 * a formatting that cannot be inferred.
 */
const FIELD_CONFIG: Record<string, FieldConfig> = {
  numberOfPeople: { label: 'Number of People' },
  wbsCode: { label: 'WBS Code' },
  startTime: { kind: 'date' },
  endTime: { kind: 'date' },
  status: { label: 'Status', kind: 'status' },
  bookingStatus: { label: 'Status', kind: 'status' },
  services: { kind: 'services' },
  customSetupTimeBefore: { label: 'Custom Setup Before (min)' },
  customSetupTimeAfter: { label: 'Custom Setup After (min)' },
  onBehalfParticipants: { label: 'On Behalf', kind: 'participants' },
};

/** Same idea as {@link FIELD_CONFIG}, for the nested change groups. */
const GROUP_CONFIG: Record<string, GroupConfig> = {
  requester: { label: 'Requester' },
  generalQuestions: {
    label: 'General Questions',
    fields: {
      cecOnSite: { label: 'CEC On Site' },
      hcpOnSite: { label: 'HCP On Site' },
    },
  },
  layout: {
    label: 'Layout',
    fields: {
      roomLayoutName: { label: 'Name' },
      roomLayoutUUID: { label: 'UUID' },
      setupBeforeMinutes: { label: 'Setup Before (min)' },
      setupAfterMinutes: { label: 'Setup After (min)' },
      capacityMax: { label: 'Max Capacity' },
      capacityMin: { label: 'Min Capacity' },
      layoutSelectedTimeDate: { label: 'Selected Date', kind: 'date' },
    },
  },
};

/** Preferred reading order; unlisted keys keep their payload order, last. */
const FIELD_ORDER = [
  'eventName',
  'numberOfPeople',
  'costCenter',
  'wbsCode',
  'userEmail',
  'startTime',
  'endTime',
  'status',
  'bookingStatus',
  'services',
  'customSetupTimeBefore',
  'customSetupTimeAfter',
  'additionalInfo',
  'message',
  'onBehalfParticipants',
];

const GROUP_ORDER = ['requester', 'generalQuestions', 'layout'];

const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

@Component({
  selector: 'bad-booking-history',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    NgTemplateOutlet,
    RdsAvatarComponent,
    RdsBadgeComponent,
    RdsIconComponent,
    RdsEmptyStateComponent,
    RdsEmptyStateTitleDirective,
  ],
  templateUrl: './booking-history.component.html',
  styleUrl: './booking-history.component.scss',
})
export class BookingHistoryComponent {
  readonly entries = input.required<BookingHistoryEntryModel[]>();
  readonly siteTimeZone = input<string | null>(null);

  protected readonly viewModel = computed<HistoryEntryViewModel[]>(() =>
    [...this.entries()]
      .sort((a, b) => b.revisionNumber - a.revisionNumber)
      .map(entry => this.toViewModel(entry))
  );

  /** Maps a raw {@link BookingHistoryEntryModel} to the template-friendly view model. */
  private toViewModel(entry: BookingHistoryEntryModel): HistoryEntryViewModel {
    const isInitial = entry.revisionNumber === 0;
    const status = statusConverter(entry.bookingStatus, entry.isReapproved);

    return {
      revisionNumber: entry.revisionNumber,
      header: isInitial ? 'Booking created' : `Change #${entry.revisionNumber}`,
      updatedBy: entry.updatedBy,
      updateDate: entry.updateDate,
      statusText: status.text,
      statusColor: status.color,
      groups: this.toGroups(entry.changes, isInitial, {}, '', ''),
    };
  }

  /**
   * Walks the changes tree and flattens it into renderable sections: the leaf
   * fields of `changes` form one section, and every nested object becomes a
   * section of its own. Nesting is followed to any depth, so a group the API
   * starts sending tomorrow renders without a template change.
   */
  private toGroups(
    changes: BookingHistoryChangesModel,
    isInitial: boolean,
    config: GroupConfig,
    path: string,
    label: string
  ): ChangeGroupViewModel[] {
    const isRoot = path === '';
    const fieldConfig = isRoot ? FIELD_CONFIG : (config.fields ?? {});
    const leafKeys: string[] = [];
    const groupKeys: string[] = [];
    for (const key of Object.keys(changes)) {
      (this.isGroup(changes[key]) ? groupKeys : leafKeys).push(key);
    }

    const groups: ChangeGroupViewModel[] = [];
    const fields = this.orderKeys(
      leafKeys,
      isRoot ? FIELD_ORDER : Object.keys(fieldConfig)
    )
      .map(key =>
        this.toField(
          key,
          changes[key] as BookingHistoryChangeModel,
          fieldConfig[key] ?? {},
          isInitial
        )
      )
      .filter((field): field is ChangeFieldViewModel => !!field);

    if (fields.length > 0) {
      groups.push({ key: path, label, fields });
    }

    for (const key of this.orderKeys(groupKeys, isRoot ? GROUP_ORDER : [])) {
      const childConfig = (isRoot ? GROUP_CONFIG[key] : undefined) ?? {};
      const childLabel = childConfig.label ?? this.toLabel(key);
      groups.push(
        ...this.toGroups(
          changes[key] as BookingHistoryChangesModel,
          isInitial,
          childConfig,
          isRoot ? key : `${path}.${key}`,
          label ? `${label} / ${childLabel}` : childLabel
        )
      );
    }

    return groups;
  }

  /** Builds one renderable field, or `undefined` when the leaf carries nothing. */
  private toField(
    key: string,
    raw: BookingHistoryChangeModel,
    config: FieldConfig,
    isInitial: boolean
  ): ChangeFieldViewModel | undefined {
    const change = this.leaf(raw);
    if (!change) {
      return undefined;
    }
    const kind = config.kind ?? this.inferKind(change);
    const label = config.label ?? this.toLabel(key);

    return kind === 'participants'
      ? {
          key,
          label,
          participants: this.toParticipantsChange(change, isInitial),
        }
      : {
          key,
          label,
          value: this.toChangeValue(change, kind, isInitial, key),
        };
  }

  /**
   * Picks a formatting for a field that has no explicit configuration: lists
   * are participants, ISO timestamps are dates, everything else is plain text.
   */
  private inferKind(change: BookingHistoryChangeModel): ChangeKind {
    const value = change.current ?? change.previous;
    if (Array.isArray(value)) {
      return 'participants';
    }
    if (typeof value === 'string' && ISO_DATE_TIME.test(value)) {
      return 'date';
    }
    return 'text';
  }

  /**
   * Turns `camelCase` / `snake_case` / `kebab-case` keys into a readable label,
   * e.g. `onBehalfParticipants` → `On Behalf Participants`.
   */
  private toLabel(key: string): string {
    return key
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .replace(/([a-zA-Z])(\d)/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .trim()
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /** Orders keys by {@link preferred}, leaving unlisted ones in payload order at the end. */
  private orderKeys(keys: string[], preferred: string[]): string[] {
    const rank = (key: string) => {
      const index = preferred.indexOf(key);
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    };
    return [...keys].sort((a, b) => rank(a) - rank(b));
  }

  /** A value is a nested group when it is an object without change sides. */
  private isGroup(
    value: BookingHistoryChangeModel | BookingHistoryChangesModel | undefined
  ): boolean {
    return !!value && !('current' in value) && !('previous' in value);
  }

  /**
   * Extracts a leaf change entry (`{current, previous}`) from a raw value.
   * Returns `undefined` when the value is missing, is a nested group,
   * or has no meaningful content.
   */
  private leaf(
    raw: BookingHistoryChangeModel | BookingHistoryChangesModel | undefined
  ): BookingHistoryChangeModel | undefined {
    if (!raw || !('current' in raw || 'previous' in raw)) {
      return undefined;
    }
    const change = raw as BookingHistoryChangeModel;
    return change.current != null || change.previous != null
      ? change
      : undefined;
  }

  /**
   * Narrows a raw change value to its scalar form. List values (participants)
   * are rendered by {@link toParticipantsChange} instead.
   */
  private asScalar(
    value: BookingHistoryChangeValueModel | undefined
  ): string | undefined {
    return typeof value === 'string' ? value : undefined;
  }

  /**
   * Formats a plain leaf value for display:
   * - empty values (`null`, `undefined`, `''`) become `'none'`
   * - boolean strings (`'true'` / `'false'`) become `'yes'` / `'no'`
   */
  private formatValue(value: string | undefined | null): string {
    if (value == null || value === '') {
      return 'none';
    }
    if (value === 'true') {
      return 'yes';
    }
    if (value === 'false') {
      return 'no';
    }
    return value;
  }

  /**
   * Converts a scalar change leaf to a {@link ChangeValue}. On an actual change
   * a missing `previous` renders as `'none'` so the template shows
   * `none → current`; the revision 0 "Booking created" snapshot is left as a
   * plain value — it is a creation, not a transition. Dates keep their raw ISO
   * strings, which the template feeds to `DatePipe`.
   */
  private toChangeValue(
    change: BookingHistoryChangeModel,
    kind: ChangeKind,
    isInitial: boolean,
    key: string
  ): ChangeValue {
    if (kind === 'date') {
      return {
        previous: this.toDateValue(this.asScalar(change.previous), key),
        current: this.toDateValue(this.asScalar(change.current), key) ?? '',
        isDate: true,
      };
    }
    const format = this.formatterFor(kind);
    return {
      previous:
        change.previous === undefined
          ? isInitial
            ? undefined
            : 'none'
          : this.formatValue(format(this.asScalar(change.previous))),
      current: this.formatValue(format(this.asScalar(change.current))),
      isDate: false,
    };
  }

  private toDateValue(
    value: string | undefined,
    key: string
  ): Date | string | undefined {
    if (!value) {
      return undefined;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    if ((key === 'startTime' || key === 'endTime') && this.siteTimeZone()) {
      return utcDateToTimezoneDisplayDate(parsed, this.siteTimeZone());
    }
    return parsed;
  }

  private formatterFor(
    kind: ChangeKind
  ): (value: string | undefined) => string | undefined {
    switch (kind) {
      case 'status':
        return value => this.formatStatus(value);
      case 'services':
        return value => this.formatServices(value);
      default:
        return value => value;
    }
  }

  /** Maps a status enum to its human-readable label (`IN_PROGRESS` → `Awaiting Approval`). */
  private formatStatus(value: string | undefined): string | undefined {
    if (value == null) {
      return undefined;
    }
    const allowed = Object.values(BookingStatusApiResponseEnum) as string[];
    return allowed.includes(value)
      ? statusConverter(value as BookingStatusApiResponseEnum).text
      : value;
  }

  /**
   * The API serialises services as a JSON array string
   * (e.g. `'[{"serviceType":"CATERING"}]'`); this renders it as a
   * comma-separated list of service types, or `'none'`.
   */
  private formatServices(value: string | undefined): string | undefined {
    if (value == null) {
      return undefined;
    }
    try {
      const parsed = JSON.parse(value) as Array<{ serviceType?: string }>;
      if (!Array.isArray(parsed) || parsed.length === 0) {
        return 'none';
      }
      const types = parsed
        .map(item => item?.serviceType)
        .filter((type): type is string => !!type);
      return types.length > 0 ? types.join(', ') : 'none';
    } catch {
      return value;
    }
  }

  /**
   * Converts a list-valued leaf (e.g. `onBehalfParticipants`) to a
   * {@link ParticipantsChangeValue}. On an actual change a missing `previous`
   * becomes an empty list, so the template renders `none → …`; the revision 0
   * snapshot keeps it undefined and shows the list on its own.
   */
  private toParticipantsChange(
    change: BookingHistoryChangeModel,
    isInitial: boolean
  ): ParticipantsChangeValue {
    return {
      previous:
        change.previous === undefined && isInitial
          ? undefined
          : this.toParticipantList(change.previous),
      current: this.toParticipantList(change.current),
    };
  }

  /**
   * Normalises one side of a participants change into display rows. A
   * participant without a full name falls back to its email as the name so a
   * row is never blank.
   */
  private toParticipantList(
    value: BookingHistoryChangeValueModel | undefined
  ): ParticipantValue[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return value
      .map(participant => this.toParticipant(participant))
      .filter((participant): participant is ParticipantValue => !!participant);
  }

  private toParticipant(
    participant: BookingHistoryParticipantModel | string
  ): ParticipantValue | undefined {
    // A list the API starts sending as plain strings still renders.
    if (typeof participant === 'string') {
      return participant.trim() ? { name: participant.trim() } : undefined;
    }
    const fullName = participant.fullName?.trim();
    const email = participant.email?.trim();
    if (fullName) {
      return { name: fullName, email };
    }
    return email ? { name: email } : undefined;
  }
}
